'use client';

import './project-item.scss';
import Link from 'next/link';
import { ProjectModel } from '@/lib/models/Project';
import { projectPath } from '@/lib/routes';
import { useSwiperSlide } from 'swiper/react';
import { useEffect, useRef, useState } from 'react';
import { useScrambleIn } from '@/lib/hooks/useScrambleIn';
import { useVerticalSwipe } from '@/lib/hooks/useVerticalSwipe';

const SWIPE_UP_THRESHOLD_PX = 100;

interface Props {
  project: ProjectModel;
  index: number;
  goToDetail: (project: ProjectModel) => void;
}

export default function ProjectMobileItem (props: Props) {
  const { project, goToDetail } = props;
  const { onTouchStart, onTouchEnd } = useVerticalSwipe({
    onSwipeUp: () => goToDetail(project),
    threshold: SWIPE_UP_THRESHOLD_PX,
  });
  const swiperSlide = useSwiperSlide();
  const shouldLoadVideo = swiperSlide.isActive || swiperSlide.isPrev || swiperSlide.isNext;
  const rootRef = useRef<HTMLDivElement>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const [hasError, setHasError] = useState(false);
  const showFallback = hasError || !project.mobileThumbnailVideo;

  useScrambleIn(rootRef, { 
    active: swiperSlide.isActive, 
    durationMs: [660, 660], 
    uppercase: true 
  });

  // Re-trigger playback when this slide becomes active. The `autoPlay`
  // attribute only fires on initial source load, so a video mounted while
  // off-screen (prev/next) never starts once centered. Calling play() here
  // also recovers from autoplay being deferred under CPU/network throttling.
  useEffect(() => {
    const video = videoRef.current;
    if (swiperSlide.isActive && video) {
      video.play().catch(() => {});
    }
    // Allow a retry next time the slide comes back into range.
    if (!shouldLoadVideo && hasError) {
      setHasError(false);
    }
  }, [swiperSlide.isActive, shouldLoadVideo, hasError]);

  return (
    <div
      ref={rootRef}
      className={'project-slide'}
      onTouchStart={onTouchStart}
      onTouchEnd={onTouchEnd}
    >
      <div className="project-slide-info">
        <dl className='project-slide-meta'>
          <dt className='project-slide-label'>CLIENT</dt>
          <dd className='project-slide-client' data-scramble-in>{project.clientName}</dd>
          <dt className='project-slide-label'>EXPERIMENT</dt>
          <dd className={`project-slide-name`}><h2 data-scramble-in>{project.mobileProjectTitle || project.projectTitle}</h2></dd>
        </dl>
      </div>
      
      <div className="project-slide-thumb-area">
        <Link
          className='project-slide-link'
          href={projectPath(project.slug)}
          aria-label={`View ${project.projectTitle} project details`}
          prefetch={false}
          onNavigate={(e) => {
            e.preventDefault();
            goToDetail(project);
          }}
        >
          <div className='project-slide-thumb'>
            {showFallback ? (
              <div
                className="project-slide-thumb-fallback"
                role="img"
                aria-label={`${project.projectTitle} thumbnail unavailable`}
              />
            ) : (
              <video
                ref={videoRef}
                autoPlay={shouldLoadVideo}
                loop 
                muted
                playsInline
                src={shouldLoadVideo ? project.mobileThumbnailVideo : undefined}
                preload={shouldLoadVideo ? 'auto' : 'none'}
                aria-hidden="true"
                onError={() => { if (shouldLoadVideo) setHasError(true); }}
              >
                <track
                  kind="captions"
                  src="/captions/no-audio.vtt"
                  srcLang="en"
                  label="No spoken audio"
                />
              </video>
            )}
          </div>
        </Link>
      </div>
    </div>
  )
};
